import type { FC } from "react";
import { useEffect, useState } from "react";

import { useUser } from "@ui/hooks/useUser";
import { useIsAuthPage } from "@ui/providers/AuthRoutingProvider";

import { useConfig } from "@jti/app-context/useConfig";
import { isServerSide } from "@jti/fe-common";
import { CasAuthApi } from "@jti/rest-api";

import { useLogOut } from "../../hooks/useLogOut";

import type { UserIndicationButtonProps } from "./components/UserIndicationButton/UserIndicationButton";
import { UserIndicationButton } from "./components/UserIndicationButton/UserIndicationButton";

export type UserActionLink = {
  title: string;
  href: string;
};

export type UserLoggedWithPending = "logged" | "notLogged" | "pending";

export const UserActions: FC<UserIndicationButtonProps> = ({
  isEnabledUserIndication,
  isEnabledUserNameForIndication,
  ...props
}) => {
  const config = useConfig();
  const currentPathname = !isServerSide() ? window.location.pathname : "";
  const isAuthPage = useIsAuthPage(currentPathname);
  const [userLoggedState, setUserLoggedState] = useState<UserLoggedWithPending>("pending");
  const { user } = useUser();
  const onLogOut = useLogOut(props.onLogOut);

  useEffect(() => {
    const validateUserSession = async () => {
      if (isAuthPage) {
        setUserLoggedState("notLogged");
        return;
      }

      if (user?.ciam_token) {
        try {
          const { statusCode } = await CasAuthApi.init(config).validate(user.ciam_token, true);
          const isValidSession = statusCode === 200 || statusCode === 204;
          const loggedState = isValidSession ? "logged" : "notLogged";

          if (!isValidSession) {
            onLogOut();
          }
          setUserLoggedState(loggedState);
        } catch {
          setUserLoggedState("notLogged");
          onLogOut();
        }
      } else {
        setUserLoggedState("notLogged");
      }
    };

    if (user) {
      validateUserSession();
    }
  }, [config, isAuthPage, onLogOut, user]);

  const isUserLoggedIn = userLoggedState === "logged";

  return (
    <UserIndicationButton
      isEnabledUserIndication={isEnabledUserIndication}
      isEnabledUserNameForIndication={isEnabledUserNameForIndication}
      isUserLoggedIn={isUserLoggedIn}
      isUserPending={userLoggedState === "pending"}
      user={user}
      isAuthPage={isAuthPage}
      {...props}
    />
  );
};
